import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash2, ChevronLeft, ChevronRight, Clock, Bell, Sparkles, Download, Loader2, Check, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0-23, all 24 hours
const CATEGORY_COLORS = {
  fixed: '#6366F1', assignment: '#EF4444', learning: '#10B981',
  meeting: '#F59E0B', social: '#EC4899', personal: '#8B5CF6', default: '#64748B'
};
const PALETTE = ['#6366F1', '#EF4444', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#A855F7'];

// Defensive helpers
const safeHour = (dt) => {
  if (!dt || typeof dt !== 'string') return null;
  const parts = dt.split('T');
  if (parts.length < 2) return null;
  const h = parseInt(parts[1].split(':')[0]);
  return isNaN(h) ? null : h;
};
const safeDate = (dt) => {
  if (!dt || typeof dt !== 'string') return null;
  return dt.split('T')[0] || null;
};
const safeTime = (dt) => {
  if (!dt || typeof dt !== 'string') return '';
  const parts = dt.split('T');
  return parts.length > 1 ? parts[1].substring(0, 5) : '';
};

const EventForm = ({ data, setData, onSubmit, label, t, theme }) => (
    <div className="space-y-4">
      <Input data-testid="event-title-input" placeholder={t('event_title')} value={data.title}
        onChange={(e) => setData({ ...data, title: e.target.value })}
        className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>{t('starts')}</label>
          <Input type="datetime-local" value={data.start_time} onChange={(e) => setData({ ...data, start_time: e.target.value })}
            className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
        </div>
        <div>
          <label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>{t('ends')}</label>
          <Input type="datetime-local" value={data.end_time} onChange={(e) => setData({ ...data, end_time: e.target.value })}
            className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
        </div>
      </div>
      <Select value={data.category || 'personal'} onValueChange={(v) => setData({ ...data, category: v })}>
        <SelectTrigger className="text-white" style={{ background: theme.elevated, borderColor: theme.border }}><SelectValue /></SelectTrigger>
        <SelectContent style={{ background: theme.card, borderColor: theme.border }}>
          <SelectItem value="fixed">{t('fixed_act')}</SelectItem>
          <SelectItem value="assignment">{t('assignment')}</SelectItem>
          <SelectItem value="learning">{t('learning')}</SelectItem>
          <SelectItem value="meeting">{t('meeting')}</SelectItem>
          <SelectItem value="social">{t('social')}</SelectItem>
          <SelectItem value="personal">{t('personal')}</SelectItem>
        </SelectContent>
      </Select>
      <Textarea placeholder={t('description_label')} value={data.description || ''} onChange={(e) => setData({ ...data, description: e.target.value })}
        className="min-h-[60px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
      {/* Custom color palette */}
      <div>
        <label className="text-xs mb-1.5 block" style={{ color: theme.textMuted }}>{t('custom_color')}</label>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button type="button" onClick={() => setData({ ...data, color: null })}
            className="w-6 h-6 rounded-full flex items-center justify-center text-[9px]"
            style={{ background: theme.elevated, border: `1.5px solid ${!data.color ? theme.accent : theme.border}`, color: theme.textMuted }}>
            auto
          </button>
          {PALETTE.map(c => (
            <button key={c} type="button" onClick={() => setData({ ...data, color: c })}
              className="w-6 h-6 rounded-full transition-transform hover:scale-110"
              style={{ background: c, border: data.color === c ? `2px solid ${theme.textPrimary}` : 'none' }} />
          ))}
        </div>
      </div>
      <Button onClick={onSubmit} className="w-full text-white" style={{ background: theme.primary }}>{label}</Button>
    </div>
  );

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isAILoading, setIsAILoading] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', start_time: '', end_time: '', description: '', reminders: [], category: 'personal' });
  const [aiData, setAiData] = useState({ fixed_activities: [], assignments: [], skills: [], meetings: [], social: [] });
  const [aiInputs, setAiInputs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('teko-ai-inputs') || '{"fixed":"","assignments":"","skills":"","meetings":"","social":""}'); }
    catch { return { fixed: '', assignments: '', skills: '', meetings: '', social: '' }; }
  });
  // Persist aiInputs
  useEffect(() => { localStorage.setItem('teko-ai-inputs', JSON.stringify(aiInputs)); }, [aiInputs]);
  const { theme } = useTheme();
  const { t, dateLocale } = useLanguage();

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try { setEvents((await api.get('/calendar')).data); } catch { toast.error('Error al cargar'); }
  };

  const addEvent = async () => {
    if (!newEvent.title || !newEvent.start_time || !newEvent.end_time) { toast.error('Completa los campos requeridos'); return; }
    try {
      await api.post('/calendar', newEvent);
      toast.success('Evento agregado');
      setNewEvent({ title: '', start_time: '', end_time: '', description: '', reminders: [], category: 'personal' });
      setShowAddDialog(false);
      fetchEvents();
    } catch { toast.error('Error'); }
  };

  const updateEvent = async () => {
    if (!editingEvent) return;
    try {
      await api.put(`/calendar/${editingEvent.id}`, {
        title: editingEvent.title, start_time: editingEvent.start_time,
        end_time: editingEvent.end_time, description: editingEvent.description,
        reminders: editingEvent.reminders || [], category: editingEvent.category || 'personal',
        color: editingEvent.color || null,
      });
      toast.success('Actualizado'); setShowEditDialog(false); setEditingEvent(null); fetchEvents();
    } catch { toast.error('Error'); }
  };

  const deleteEvent = async (id) => {
    try { await api.delete(`/calendar/${id}`); toast.success('Eliminado'); fetchEvents(); } catch { toast.error('Error'); }
  };

  const toggleEventComplete = async (id) => {
    try {
      const { data } = await api.put(`/calendar/${id}/toggle-complete`);
      fetchEvents();
    } catch { toast.error('Error'); }
  };

  const resetWeek = async () => {
    const weekStrs = getWeekDays().map(d => d.toISOString().split('T')[0]);
    const toDelete = events.filter(e => {
      const ds = safeDate(e.start_time);
      const cat = e.description?.match(/\[(\w+)\]/)?.[1] || e.category || 'personal';
      return ds && weekStrs.includes(ds) && cat !== 'fixed';
    });
    try {
      for (const ev of toDelete) await api.delete(`/calendar/${ev.id}`);
      toast.success(`${toDelete.length} eventos eliminados (fijos conservados)`);
      fetchEvents();
    } catch { toast.error('Error'); }
  };

  const openAddAtSlot = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    const hh = String(hour).padStart(2, '0');
    const nextHh = String((hour + 1) % 24).padStart(2, '0');
    setNewEvent({ title: '', start_time: `${dateStr}T${hh}:00`, end_time: `${dateStr}T${nextHh}:00`, description: '', reminders: [], category: 'personal' });
    setShowAddDialog(true);
  };


  const handleAIOrganize = async () => {
    setIsAILoading(true);
    try {
      const payload = {
        fixed_activities: aiInputs.fixed.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim() })),
        assignments: aiInputs.assignments.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim(), urgency: 'medium' })),
        skills: aiInputs.skills.split('\n').filter(l => l.trim()),
        meetings: aiInputs.meetings.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim() })),
        social: aiInputs.social.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim() })),
      };
      const { data } = await api.post('/ai/organize-calendar', payload);
      if (data.blocks?.length > 0) {
        const weekStart = getWeekStart();
        const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
        for (const block of data.blocks) {
          const dayOffset = dayMap[block.day?.toLowerCase()] || 0;
          const eventDate = new Date(weekStart);
          eventDate.setDate(eventDate.getDate() + dayOffset);
          const dateStr = eventDate.toISOString().split('T')[0];
          await api.post('/calendar', {
            title: block.title, start_time: `${dateStr}T${block.start_time}`,
            end_time: `${dateStr}T${block.end_time}`, description: `[${block.category}] ${block.priority || ''}`,
            category: block.category || 'personal'
          });
        }
        toast.success(`${data.blocks.length} bloques creados`);
        setShowAIDialog(false);
        fetchEvents();
      } else { toast.error('No se generaron bloques'); }
    } catch (e) { toast.error('Error al organizar'); }
    finally { setIsAILoading(false); }
  };

  const getWeekStart = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - d.getDay());
    return d;
  };

  const getWeekDays = () => {
    const start = getWeekStart();
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  };

  const getEventsForHour = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => {
      const ds = safeDate(e.start_time);
      if (ds !== dateStr) return false;
      const sh = safeHour(e.start_time);
      const eh = safeHour(e.end_time);
      if (sh === null) return false;
      const endH = eh !== null ? eh : sh + 1;
      return sh <= hour && endH > hour;
    });
  };

  const getEventSpan = (event) => {
    const sh = safeHour(event.start_time) ?? 0;
    const eh = safeHour(event.end_time);
    return Math.max(1, (eh !== null ? eh : sh + 1) - sh);
  };

  const getEventColor = (event) => {
    if (event.color) return event.color; // user override
    const cat = event.description?.match(/\[(\w+)\]/)?.[1] || event.category || 'default';
    return CATEGORY_COLORS[cat] || theme.primary;
  };

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => safeDate(e.start_time) === dateStr);
  };

  const weekDays = getWeekDays();
  const prevPeriod = () => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else d.setDate(d.getDate() - (view === 'week' ? 7 : 1));
    setCurrentDate(d);
  };
  const nextPeriod = () => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else d.setDate(d.getDate() + (view === 'week' ? 7 : 1));
    setCurrentDate(d);
  };

  const weekRange = `${weekDays[0].toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' })} - ${weekDays[6].toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  const dayLabel = currentDate.toLocaleDateString(dateLocale, { weekday: 'long', day: 'numeric', month: 'long' });
  const monthLabel = currentDate.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' });

  // Month grid: 6 weeks × 7 days starting from Sunday
  const getMonthGrid = () => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const startOffset = first.getDay();
    const start = new Date(first);
    start.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return d;
    });
  };

  const exportCalendar = async () => {
    try {
      const response = await api.get('/export/calendar', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'calendar_export.csv'; a.click();
      toast.success('Exportado');
    } catch { toast.error('Error'); }
  };



  return (
    <div data-testid="calendar-page" className="space-y-6">
      {/* Hero header with gradient */}
      <div className="relative overflow-hidden rounded-2xl p-6 md:p-8"
        style={{ background: theme.gradientSoft || `linear-gradient(135deg, ${theme.primary}30, ${theme.card})`, border: `1px solid ${theme.primary}30` }}>
        <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: theme.accent }} />
        <div className="relative z-10 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-[2px] w-8 rounded-full" style={{ background: theme.accent }} />
              <p className="text-xs uppercase tracking-[0.25em]" style={{ color: theme.textSecondary }}>{t('your_time')}</p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter" style={{ color: theme.textPrimary }}>{t('calendar')}</h1>
          </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button data-testid="ai-calendar-button" className="text-white" style={{ background: theme.primary }}>
                <Sparkles size={18} className="mr-2" />{t('ai_assistant')}
              </Button>
            </DialogTrigger>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} className="max-w-2xl max-h-[90vh] overflow-y-auto"
              style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <DialogHeader><DialogTitle className="text-2xl" style={{ color: theme.accent }}>Organizar Semana con IA</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: theme.textPrimary }}>Actividades con horario fijo</label>
                  <p className="text-xs mb-2" style={{ color: theme.textMuted }}>Trabajo, universidad, gimnasio, etc. (una por línea)</p>
                  <Textarea value={aiInputs.fixed} onChange={(e) => setAiInputs({ ...aiInputs, fixed: e.target.value })}
                    placeholder="Trabajo 9am-5pm lunes a viernes&#10;Gimnasio 6pm-7pm lunes, miércoles, viernes"
                    className="min-h-[80px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: theme.textPrimary }}>Asignaciones importantes</label>
                  <p className="text-xs mb-2" style={{ color: theme.textMuted }}>Tareas y su nivel de urgencia</p>
                  <Textarea value={aiInputs.assignments} onChange={(e) => setAiInputs({ ...aiInputs, assignments: e.target.value })}
                    placeholder="Entregar proyecto final (urgente)&#10;Estudiar para examen (medio)"
                    className="min-h-[80px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: theme.textPrimary }}>Habilidades a aprender</label>
                  <Textarea value={aiInputs.skills} onChange={(e) => setAiInputs({ ...aiInputs, skills: e.target.value })}
                    placeholder="Programación en Python&#10;Tocar guitarra"
                    className="min-h-[60px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: theme.textPrimary }}>Reuniones de trabajo</label>
                  <Textarea value={aiInputs.meetings} onChange={(e) => setAiInputs({ ...aiInputs, meetings: e.target.value })}
                    placeholder="Standup diario 9:30am&#10;Revisión semanal viernes 3pm"
                    className="min-h-[60px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: theme.textPrimary }}>Actividades sociales</label>
                  <Textarea value={aiInputs.social} onChange={(e) => setAiInputs({ ...aiInputs, social: e.target.value })}
                    placeholder="Cena con amigos sábado&#10;Fútbol domingo mañana"
                    className="min-h-[60px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setAiInputs({ fixed: '', assignments: '', skills: '', meetings: '', social: '' })}
                    variant="outline" className="shrink-0" style={{ borderColor: theme.border, color: theme.error }}
                    title="Limpiar todos los campos">
                    <RotateCcw size={16} className="mr-1" />Limpiar
                  </Button>
                  <Button onClick={handleAIOrganize} disabled={isAILoading} className="flex-1 text-white" style={{ background: theme.primary }}>
                    {isAILoading ? <><Loader2 size={18} className="mr-2 animate-spin" />Organizando...</> : 'Organizar Semana'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button onClick={exportCalendar} variant="outline" style={{ borderColor: theme.border, color: theme.textSecondary }}>
            <Download size={18} className="mr-2" />CSV
          </Button>
          <Button onClick={resetWeek} variant="outline" style={{ borderColor: theme.border, color: theme.error }}>
            <RotateCcw size={18} className="mr-2" />{t('reset_week')}
          </Button>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="text-white w-[120px]" style={{ background: theme.card, borderColor: theme.border }}><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: theme.card, borderColor: theme.border }}>
              <SelectItem value="day">{t('day')}</SelectItem>
              <SelectItem value="week">{t('week')}</SelectItem>
              <SelectItem value="month">{t('month')}</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="text-white" style={{ background: theme.primary }}><Plus size={18} className="mr-2" />{t('new_event')}</Button>
            </DialogTrigger>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <DialogHeader><DialogTitle className="text-2xl" style={{ color: theme.accent }}>{t('new_event')}</DialogTitle></DialogHeader>
              <EventForm t={t} theme={theme} data={newEvent} setData={setNewEvent} onSubmit={addEvent} label={t('add')} />
            </DialogContent>
          </Dialog>
        </div>
        </div>
      </div>

      {/* Edit Dialog - with DELETE button */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent onPointerDownOutside={(e) => e.preventDefault()} style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center justify-between" style={{ color: theme.accent }}>
              <span>{t('edit_event')}</span>
              {editingEvent && (
                <button data-testid="delete-event-btn" onClick={async () => {
                  try {
                    await api.delete(`/calendar/${editingEvent.id}`);
                    toast.success('OK');
                    setShowEditDialog(false); setEditingEvent(null); fetchEvents();
                  } catch { toast.error('Error'); }
                }} className="p-2 rounded-md text-sm flex items-center gap-1.5 transition-colors hover:scale-105" style={{ color: theme.error, background: `${theme.error}15`, border: `1px solid ${theme.error}40` }}>
                  <Trash2 size={14} /><span className="text-xs">{t('delete')}</span>
                </button>
              )}
            </DialogTitle>
          </DialogHeader>
          {editingEvent && <EventForm t={t} theme={theme} data={editingEvent} setData={setEditingEvent} onSubmit={updateEvent} label={t('update')} />}
        </DialogContent>
      </Dialog>

      {/* Calendar Header */}
      <div className="p-4 rounded-lg" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <div className="flex items-center justify-between">
          <button onClick={prevPeriod} className="p-2 rounded-md" style={{ color: theme.accent }}><ChevronLeft size={24} /></button>
          <h2 className="text-xl font-medium capitalize" style={{ color: theme.accent }}>
            {view === 'week' ? weekRange : view === 'month' ? monthLabel : dayLabel}
          </h2>
          <button onClick={nextPeriod} className="p-2 rounded-md" style={{ color: theme.accent }}><ChevronRight size={24} /></button>
        </div>
      </div>

      {/* Month View */}
      {view === 'month' && (
        <div className="rounded-xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          {/* Day headers */}
          <div className="grid grid-cols-7" style={{ background: theme.elevated }}>
            {Array.from({ length: 7 }, (_, i) => {
              const d = new Date(2024, 0, 7 + i); // a Sunday-starting reference week
              return d.toLocaleDateString(dateLocale, { weekday: 'short' });
            }).map((d, i) => (
              <div key={i} className="p-3 text-center text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: theme.textMuted, borderRight: i < 6 ? `1px solid ${theme.border}` : 'none' }}>
                {d}
              </div>
            ))}
          </div>
          {/* Grid 6 weeks */}
          <div className="grid grid-cols-7">
            {getMonthGrid().map((day, idx) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div key={idx}
                  onClick={() => { setCurrentDate(day); setView('day'); }}
                  className="min-h-[110px] p-2 cursor-pointer transition-all hover:bg-white/5 relative"
                  style={{
                    borderRight: (idx + 1) % 7 !== 0 ? `1px solid ${theme.border}` : 'none',
                    borderTop: idx >= 7 ? `1px solid ${theme.border}` : 'none',
                    background: isToday ? `${theme.primary}15` : 'transparent',
                    opacity: isCurrentMonth ? 1 : 0.4,
                  }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`inline-flex items-center justify-center text-xs font-semibold rounded-full ${isToday ? 'w-6 h-6' : ''}`}
                      style={{
                        color: isToday ? '#fff' : isCurrentMonth ? theme.textPrimary : theme.textMuted,
                        background: isToday ? theme.primary : 'transparent',
                      }}>
                      {day.getDate()}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[9px] px-1.5 rounded-full" style={{ background: `${theme.accent}20`, color: theme.accent }}>{dayEvents.length}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map(ev => {
                      const color = getEventColor(ev);
                      return (
                        <div key={ev.id}
                          onClick={(e) => { e.stopPropagation(); setEditingEvent({ ...ev }); setShowEditDialog(true); }}
                          className="text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1"
                          style={{
                            background: `${color}25`,
                            borderLeft: `2px solid ${color}`,
                            color: theme.textPrimary,
                            textDecoration: ev.completed ? 'line-through' : 'none',
                            opacity: ev.completed ? 0.6 : 1,
                          }}
                          title={`${safeTime(ev.start_time)} ${ev.title}`}>
                          <span className="opacity-70">{safeTime(ev.start_time)}</span>
                          <span className="truncate">{ev.title}</span>
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <p className="text-[9px] pl-1.5" style={{ color: theme.textMuted }}>+{dayEvents.length - 3} más</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week View */}
      {view === 'week' && (
        <div className="rounded-xl overflow-hidden shadow-lg" style={{ border: `1px solid ${theme.border}` }}>
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Day Headers */}
              <div className="grid grid-cols-8 gap-0" style={{ background: `linear-gradient(180deg, ${theme.elevated}, ${theme.card})` }}>
                <div className="p-3 text-center text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}` }}>Hora</div>
                {weekDays.map((day, i) => {
                  const isToday = day.toDateString() === new Date().toDateString();
                  return (
                    <div key={i} className="p-3 text-center transition-colors" style={{ borderRight: i < 6 ? `1px solid ${theme.border}` : 'none', background: isToday ? `${theme.primary}25` : 'transparent' }}>
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: isToday ? theme.accent : theme.textMuted }}>{day.toLocaleDateString(dateLocale, { weekday: 'short' })}</div>
                      <div className={`text-2xl font-light mt-0.5 ${isToday ? 'inline-flex items-center justify-center w-9 h-9 rounded-full' : ''}`}
                        style={{ color: isToday ? '#fff' : theme.textPrimary, background: isToday ? theme.primary : 'transparent', boxShadow: isToday ? `0 0 16px ${theme.primary}80` : 'none' }}>
                        {day.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Time Grid */}
              <div className="max-h-[600px] overflow-y-auto scrollbar-thin">
                {HOURS.map(hour => {
                  const isCurrentHour = new Date().getHours() === hour;
                  return (
                    <div key={hour} className="grid grid-cols-8 gap-0" style={{ borderTop: `1px solid ${theme.border}` }}>
                      <div className="p-2 text-right text-xs tabular-nums flex items-start justify-end pt-2.5"
                        style={{
                          color: isCurrentHour ? theme.accent : theme.textMuted,
                          fontWeight: isCurrentHour ? 600 : 400,
                          borderRight: `1px solid ${theme.border}`,
                          background: isCurrentHour ? `${theme.primary}08` : 'transparent',
                        }}>
                        {hour.toString().padStart(2, '0')}:00
                      </div>
                      {weekDays.map((day, di) => {
                        const hourEvents = getEventsForHour(day, hour);
                        const isFirst = hourEvents.filter(e => safeHour(e.start_time) === hour);
                        const isToday = day.toDateString() === new Date().toDateString();
                        return (
                          <div key={di}
                            onClick={() => { if (isFirst.length === 0) openAddAtSlot(day, hour); }}
                            className="min-h-[52px] relative cursor-pointer hover:bg-white/5 transition-colors group/cell"
                            style={{
                              borderRight: di < 6 ? `1px solid ${theme.border}` : 'none',
                              background: isToday && isCurrentHour ? `${theme.primary}10` : 'transparent',
                            }}>
                            {isFirst.length === 0 && (
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/cell:opacity-50 transition-opacity">
                                <Plus size={14} style={{ color: theme.accent }} />
                              </div>
                            )}
                            {isFirst.map(ev => {
                              const span = getEventSpan(ev);
                              const color = getEventColor(ev);
                              return (
                                <div key={ev.id} onClick={(e) => { e.stopPropagation(); setEditingEvent({ ...ev }); setShowEditDialog(true); }}
                                  className="absolute inset-x-1 rounded-md px-2 py-1.5 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg overflow-hidden z-10"
                                  style={{
                                    background: `linear-gradient(135deg, ${color}f0, ${color}b0)`,
                                    borderLeft: `3px solid ${color}`,
                                    height: `${span * 52 - 4}px`,
                                    top: '2px',
                                    boxShadow: `0 4px 12px ${color}40`,
                                    textDecoration: ev.completed ? 'line-through' : 'none',
                                    opacity: ev.completed ? 0.6 : 1,
                                  }}>
                                  <p className="text-xs font-semibold truncate" style={{ color: '#fff' }}>{ev.title}</p>
                                  <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                    {safeTime(ev.start_time)} - {safeTime(ev.end_time)}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day View - Time Blocking */}
      {view === 'day' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-lg" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="p-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <h3 className="text-lg font-medium flex items-center gap-2" style={{ color: theme.accent }}>
                <Clock size={20} /> {t('time_blocks')}
              </h3>
            </div>
            <div className="max-h-[600px] overflow-y-auto scrollbar-thin">
              {HOURS.map(hour => {
                const hourEvents = getEventsForHour(currentDate, hour);
                const isFirst = hourEvents.filter(e => safeHour(e.start_time) === hour);
                return (
                  <div key={hour} className="flex" style={{ borderTop: `1px solid ${theme.border}` }}>
                    <div className="w-20 p-3 text-right text-sm shrink-0" style={{ color: theme.textMuted }}>
                      {hour.toString().padStart(2, '0')}:00
                    </div>
                    <div className="flex-1 min-h-[64px] p-2 space-y-1 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={(e) => { if (e.target === e.currentTarget) openAddAtSlot(currentDate, hour); }}>
                      {isFirst.length === 0 && (
                        <button onClick={(e) => { e.stopPropagation(); openAddAtSlot(currentDate, hour); }}
                          className="opacity-0 hover:opacity-100 text-xs w-full h-full min-h-[48px] rounded border-2 border-dashed transition-opacity"
                          style={{ borderColor: theme.border, color: theme.textMuted }}>
                          + {t('add_event')}
                        </button>
                      )}
                      {isFirst.map(ev => {
                        const color = getEventColor(ev);
                        const isComplete = ev.completed;
                        return (
                          <div key={ev.id} onClick={() => { setEditingEvent({ ...ev }); setShowEditDialog(true); }}
                            className="flex items-center gap-3 p-3 rounded-md cursor-pointer transition-all hover:scale-[1.01]"
                            style={{ background: `${color}15`, borderLeft: `4px solid ${color}`, opacity: isComplete ? 0.5 : 1 }}>
                            <button onClick={(e) => { e.stopPropagation(); toggleEventComplete(ev.id); }}
                              className="w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all"
                              style={{ borderColor: isComplete ? theme.success : color, background: isComplete ? theme.success : 'transparent' }}>
                              {isComplete && <Check size={12} className="text-white" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${isComplete ? 'line-through' : ''}`} style={{ color: theme.textPrimary }}>{ev.title}</p>
                              <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>
                                {safeTime(ev.start_time)} - {safeTime(ev.end_time)}
                              </p>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); deleteEvent(ev.id); }} className="p-1 shrink-0" style={{ color: theme.textMuted }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day Sidebar - Focus Areas & Notes */}
          <div className="space-y-4">
            <div className="rounded-lg p-4" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: theme.accent }}>{t('day_summary')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.textSecondary }}>{t('events_lbl')}</span>
                  <span style={{ color: theme.textPrimary }}>{getEventsForDay(currentDate).length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: theme.textSecondary }}>{t('busy_hours')}</span>
                  <span style={{ color: theme.textPrimary }}>
                    {getEventsForDay(currentDate).reduce((sum, e) => {
                      const sh = safeHour(e.start_time) ?? 0;
                      const eh = safeHour(e.end_time) ?? sh;
                      return sum + Math.max(0, eh - sh);
                    }, 0)}h
                  </span>
                </div>
              </div>
            </div>

            {/* Categories breakdown */}
            <div className="rounded-lg p-4" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: theme.accent }}>{t('categories_lbl')}</h3>
              {Object.entries(CATEGORY_COLORS).filter(([k]) => k !== 'default').map(([cat, color]) => {
                const count = getEventsForDay(currentDate).filter(e => (e.description?.includes(`[${cat}]`) || e.category === cat)).length;
                if (count === 0) return null;
                return (
                  <div key={cat} className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded" style={{ background: color }} />
                    <span className="text-sm flex-1 capitalize" style={{ color: theme.textPrimary }}>{cat}</span>
                    <span className="text-sm font-medium" style={{ color: theme.accent }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
